import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Loader2,
  Users,
  Briefcase,
  Stethoscope,
  BookOpen,
  Film,
  FileText,
  Headphones,
  Wrench,
  GraduationCap,
} from "lucide-react";
import SEO from "@/components/SEO";
import { absoluteUrl } from "@/lib/seo-config";
import { fetchLearningResources, trackLearningTab } from "@/lib/api";
import { track } from "@/lib/analytics";

const TABS = [
  { key: "clients", label: "Clients", Icon: Users, blurb: "Reading and tools for anyone in care with us — pain education, recovery, posture, sleep, pregnancy." },
  { key: "aspiring_therapists", label: "Aspiring Therapists", Icon: GraduationCap, blurb: "If you're studying or considering a career in massage therapy, start here." },
  { key: "healthcare_pros", label: "Healthcare Pros", Icon: Stethoscope, blurb: "For physicians, physios, chiropractors and trainers referring clients to clinical massage." },
];

const TYPE_ICON = {
  article: BookOpen,
  video: Film,
  book: BookOpen,
  course: GraduationCap,
  pdf: FileText,
  podcast: Headphones,
  tool: Wrench,
};

const breadcrumb = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: absoluteUrl("/") },
    { "@type": "ListItem", position: 2, name: "Learning Hub", item: absoluteUrl("/learn") },
  ],
};

export default function Learn() {
  const [tab, setTab] = useState("clients");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (which) => {
    setLoading(true);
    try {
      const res = await fetchLearningResources(which);
      setData(res);
    } catch (err) {
      console.warn("Learning hub fetch failed", err);
      setData({ audience: which, manual: [], blog: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(tab);
    // Fire tab-view tracking on every change (incl. the initial mount).
    trackLearningTab(tab);
    track("learning_hub_tab_view", { tab });
  }, [tab, load]);

  const activeTab = useMemo(() => TABS.find((t) => t.key === tab), [tab]);
  const items = useMemo(() => {
    if (!data) return [];
    return [...(data.manual || []), ...(data.blog || [])];
  }, [data]);

  return (
    <main data-testid="learn-page" className="bg-white">
      <SEO
        title="Learning Hub — Ashlan Clinic"
        description="Curated resources from Ashlan Clinic for clients, aspiring massage therapists and healthcare professionals — articles, videos, books and tools."
        path="/learn"
        schemas={[breadcrumb]}
      />

      <section className="border-b border-[#e5e7eb] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-12">
          <span className="ash-eyebrow">Learning Hub</span>
          <h1 className="mt-6 text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight text-[#080808] max-w-4xl">
            Recommended reading from our team.
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#3C4048] max-w-3xl leading-relaxed">
            A growing library of articles, books, videos and tools we trust — organised by who
            it&apos;s for. Curated by our therapists.
          </p>
        </div>
      </section>

      <section className="border-b border-[#e5e7eb] bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex gap-2 overflow-x-auto" data-testid="learn-tabs">
          {TABS.map((t) => {
            const Icon = t.Icon;
            const isActive = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                data-testid={`learn-tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-5 py-4 text-sm font-semibold whitespace-nowrap transition-colors ${
                  isActive
                    ? "text-[#3A7A8C] border-b-2 border-[#3A7A8C] -mb-px"
                    : "text-[#5A5F6A] hover:text-[#080808]"
                }`}
              >
                <Icon size={15} /> {t.label}
              </button>
            );
          })}
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {activeTab && (
            <p className="text-[#3C4048] max-w-2xl leading-relaxed mb-10" data-testid="tab-blurb">
              {activeTab.blurb}
            </p>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20 text-[#5A5F6A]">
              <Loader2 className="animate-spin mr-2" /> Loading resources…
            </div>
          ) : items.length === 0 ? (
            <EmptyState audience={tab} />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="resource-grid">
              {items.map((r) => (
                <ResourceCard key={r.id} resource={r} audience={tab} />
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

function ResourceCard({ resource, audience }) {
  const TypeIcon = TYPE_ICON[resource.resource_type] || BookOpen;
  const isInternal = resource.is_internal || (resource.url || "").startsWith("/");
  const Wrapper = isInternal ? Link : "a";
  const wrapperProps = isInternal
    ? { to: resource.url }
    : { href: resource.url, target: "_blank", rel: "noopener noreferrer" };

  const onClick = () => {
    track("learning_hub_resource_click", {
      tab: audience,
      resource_id: resource.id,
      resource_type: resource.resource_type,
      is_internal: isInternal,
    });
  };

  return (
    <Wrapper
      {...wrapperProps}
      data-testid={`resource-card-${resource.id}`}
      onClick={onClick}
      className="block bg-white border border-[#e5e7eb] p-6 hover:border-[#3A7A8C] hover:-translate-y-0.5 transition-all duration-200 group"
    >
      <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-[0.18em] font-semibold text-[#3A7A8C]">
        <TypeIcon size={13} /> {resource.resource_type}
        {resource.source && (
          <span className="text-[#6B7280] normal-case tracking-normal font-normal ml-auto">{resource.source}</span>
        )}
      </div>
      <h3 className="text-lg font-bold text-[#080808] leading-snug group-hover:text-[#3A7A8C] transition-colors">
        {resource.title}
      </h3>
      {resource.description && (
        <p className="mt-3 text-sm text-[#3C4048] leading-relaxed line-clamp-3">
          {resource.description}
        </p>
      )}
      <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-[#3A7A8C]">
        {isInternal ? "Read on Ashlan" : "Open resource"} <ArrowUpRight size={13} />
      </span>
    </Wrapper>
  );
}

function EmptyState({ audience }) {
  const inquiry = audience === "healthcare_pros"
    ? "If you're a healthcare professional looking for clinical-massage referral resources, get in touch — we'll point you to the latest research and our referral pathway."
    : audience === "aspiring_therapists"
    ? "Studying or considering a career in massage therapy? Reach out — we'd love to recommend a pathway."
    : "Nothing here yet — try a different tab, or get in touch and we'll send you something tailored.";
  return (
    <div className="border border-dashed border-[#e5e7eb] p-12 text-center" data-testid="empty-state">
      <Briefcase size={28} className="mx-auto text-[#5FA8B4] mb-4" />
      <p className="text-[#3C4048] max-w-xl mx-auto leading-relaxed">{inquiry}</p>
      <Link
        to={`/contact?subject=${encodeURIComponent("Learning Hub — request a recommendation")}`}
        className="ash-link-underline mt-6 inline-flex items-center gap-1.5"
      >
        Ask our team
      </Link>
    </div>
  );
}
